// ExportFormatDialog.js - Updated with School Fees Context

import React, { useState } from 'react';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  RadioGroup,
  FormControlLabel,
  Radio,
  Box,
  Typography,
  CircularProgress,
  Alert,
  Chip
} from '@mui/material';
import { PictureAsPdf, TableChart, School } from '@mui/icons-material';
import { useSchoolFeesMode } from './SchoolFeesContext'; // ✅ Import context

const ExportFormatDialog = ({ 
  open, 
  onClose, 
  onExport,
  currentPeriod,
  groupName,
  groupId  // ✅ Add groupId prop
}) => {
  const [selectedFormat, setSelectedFormat] = useState('pdf');
  const [exporting, setExporting] = useState(false);
  const [error, setError] = useState(null);
  
  // ✅ Get school fees mode from context
  const { getModeForGroup } = useSchoolFeesMode();
  const isSchoolFees = getModeForGroup(groupId);

  const handleExport = async () => {
    setError(null);
    setExporting(true);
    
    try {
      // ✅ Pass isSchoolFees to the export function
      await onExport(selectedFormat, isSchoolFees);
      // Close dialog on success
      handleClose();
    } catch (err) {
      setError(err.message || `Failed to export ${selectedFormat.toUpperCase()}`);
    } finally {
      setExporting(false);
    }
  };

  const handleClose = () => {
    if (!exporting) {
      setError(null);
      setSelectedFormat('pdf'); // Reset to default
      onClose();
    }
  };

  const handleFormatChange = (event) => {
    setSelectedFormat(event.target.value);
  };

  return (
    <Dialog 
      open={open} 
      onClose={handleClose}
      maxWidth="sm"
      fullWidth
    >
      <DialogTitle>
        Export Performance Analysis
        {/* ✅ Show school fees indicator */}
        {isSchoolFees && (
          <Chip 
            icon={<School />} 
            label="School Fees" 
            color="secondary" 
            size="small" 
            sx={{ ml: 2 }}
          />
        )}
      </DialogTitle>
      
      <DialogContent>
        {error && (
          <Alert severity="error" sx={{ mb: 2 }} onClose={() => setError(null)}>
            {error}
          </Alert>
        )}

        <Box sx={{ mb: 2 }}>
          <Typography variant="body2" color="text.secondary" gutterBottom>
            Group: <strong>{groupName}</strong>
          </Typography>
          <Typography variant="body2" color="text.secondary" gutterBottom>
            Period: <strong>{currentPeriod?.period_name || 'Current Period'}</strong>
          </Typography>
          {/* ✅ Show account type */}
          <Typography variant="body2" color="text.secondary">
            Account Type: <strong>{isSchoolFees ? 'School Fees' : 'Regular'}</strong>
          </Typography>
        </Box>

        <Typography variant="subtitle2" sx={{ mb: 2 }}>
          Select export format:
        </Typography>

        <RadioGroup
          value={selectedFormat}
          onChange={handleFormatChange}
        >
          <Box sx={{ 
            border: '1px solid',
            borderColor: selectedFormat === 'pdf' ? 'primary.main' : 'divider',
            borderRadius: 1,
            p: 2,
            mb: 2,
            cursor: 'pointer',
            bgcolor: selectedFormat === 'pdf' ? 'primary.dark' : 'transparent',
            transition: 'all 0.2s',
            '&:hover': {
              bgcolor: selectedFormat === 'pdf' ? 'primary.dark' : 'action.hover',
              borderColor: selectedFormat === 'pdf' ? 'primary.main' : 'primary.light'
            }
          }}
          onClick={() => setSelectedFormat('pdf')}
          >
            <FormControlLabel
              value="pdf"
              control={<Radio />}
              label={
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                  <PictureAsPdf color={selectedFormat === 'pdf' ? 'primary' : 'action'} />
                  <Box>
                    <Typography variant="body1" fontWeight={selectedFormat === 'pdf' ? 'bold' : 'normal'}>
                      PDF Format
                    </Typography>
                    <Typography variant="caption" color="text.secondary">
                      Formatted document with detailed layout and styling
                    </Typography>
                  </Box>
                </Box>
              }
            />
          </Box>

          <Box sx={{ 
            border: '1px solid',
            borderColor: selectedFormat === 'excel' ? 'primary.main' : 'divider',
            borderRadius: 1,
            p: 2,
            cursor: 'pointer',
            bgcolor: selectedFormat === 'excel' ? 'primary.dark' : 'transparent',
            transition: 'all 0.2s',
            '&:hover': {
              bgcolor: selectedFormat === 'excel' ? 'primary.dark' : 'action.hover',
              borderColor: selectedFormat === 'excel' ? 'primary.main' : 'primary.light'
            }
          }}
          onClick={() => setSelectedFormat('excel')}
          >
            <FormControlLabel
              value="excel"
              control={<Radio />}
              label={
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                  <TableChart color={selectedFormat === 'excel' ? 'primary' : 'action'} />
                  <Box>
                    <Typography variant="body1" fontWeight={selectedFormat === 'excel' ? 'bold' : 'normal'}>
                      Excel Format
                    </Typography>
                    <Typography variant="caption" color="text.secondary">
                      Spreadsheet for data analysis and manipulation
                    </Typography>
                  </Box>
                </Box>
              }
            />
          </Box>
        </RadioGroup>
      </DialogContent>

      <DialogActions>
        <Button 
          onClick={handleClose} 
          disabled={exporting}
        >
          Cancel
        </Button>
        <Button 
          onClick={handleExport}
          variant="contained"
          disabled={exporting}
          startIcon={exporting ? <CircularProgress size={20} /> : null}
        >
          {exporting ? 'Exporting...' : 'Export'}
        </Button>
      </DialogActions>
    </Dialog>
  );
};

export default ExportFormatDialog;